export interface DamageTypeData {
  name: string;
  slug: string;
  title: string;
  metaDescription: string;
  heroTitle: string;
  heroSubtitle: string;
  description: string;
  statistics: {
    label: string;
    value: string;
    description: string;
  }[];
  howItWorks: {
    title: string;
    description: string;
    steps: {
      title: string;
      description: string;
    }[];
  };
  realCases: {
    title: string;
    location: string;
    damage: string;
    description: string;
    consequences: string[];
  }[];
  insuranceProtection: {
    title: string;
    coverages: {
      name: string;
      description: string;
      example: string;
    }[];
  };
  bestTariffs: {
    tariff: string;
    provider: string;
    coverage: string;
    highlight: string;
  }[];
  prevention: {
    title: string;
    measures: {
      category: string;
      actions: string[];
    }[];
  };
  faqs: {
    question: string;
    answer: string;
  }[];
}

export const damageTypes: Record<string, DamageTypeData> = {
  "ransomware-schutz": {
    name: "Ransomware-Schutz",
    slug: "ransomware-schutz",
    title: "Ransomware-Schutz für Arztpraxen - Cyberversicherung gegen Erpressung",
    metaDescription: "Spezialisierte Cyberversicherung gegen Ransomware-Angriffe auf Arztpraxen. Schutz vor Verschlüsselung, Lösegeldforderungen und Betriebsausfall. Jetzt absichern.",
    heroTitle: "Ransomware-Schutz für Arztpraxen",
    heroSubtitle: "Umfassender Versicherungsschutz gegen Erpressungstrojaner und Datenverschlüsselung",
    description: "Ransomware ist die größte Cyber-Bedrohung für Arztpraxen. Kriminelle verschlüsseln Ihre Patientendaten und fordern Lösegeld. Ohne Schutz drohen Totalausfall, Datenverlust und existenzbedrohende Kosten. Eine spezialisierte Cyberversicherung schützt Sie umfassend.",
    statistics: [
      {
        label: "Angriffe auf Gesundheitswesen",
        value: "+94%",
        description: "Anstieg von Ransomware-Attacken 2024"
      },
      {
        label: "Durchschnittliches Lösegeld",
        value: "25.000€",
        description: "Geforderte Summe bei Arztpraxen"
      },
      {
        label: "Ausfallzeit",
        value: "18 Tage",
        description: "Durchschnittliche Betriebsunterbrechung"
      },
      {
        label: "Gesamtschaden",
        value: "85.000€",
        description: "Ø Kosten ohne Versicherungsschutz"
      }
    ],
    howItWorks: {
      title: "So läuft ein Ransomware-Angriff ab",
      description: "Verstehen Sie die Angriffsmechanismen, um sich besser zu schützen:",
      steps: [
        {
          title: "Infektion",
          description: "Mitarbeiter öffnet präparierte E-Mail oder klickt auf manipulierten Link. Schadsoftware installiert sich unbemerkt."
        },
        {
          title: "Ausbreitung",
          description: "Der Trojaner verbreitet sich im Netzwerk, sucht nach Servern, Backups und wichtigen Dateien."
        },
        {
          title: "Verschlüsselung",
          description: "Alle Patientenakten, Röntgenbilder, Abrechnungsdaten werden verschlüsselt. Zugriff ist unmöglich."
        },
        {
          title: "Erpressung",
          description: "Erpresser-Nachricht erscheint: 'Zahlen Sie 25.000€ in Bitcoin oder verlieren Sie alle Daten.'"
        },
        {
          title: "Eskalation",
          description: "Ohne Zahlung drohen Datenveröffentlichung im Darknet und permanente Löschung nach 72 Stunden."
        }
      ]
    },
    realCases: [
      {
        title: "Hausarztpraxis München lahmgelegt",
        location: "München, 2024",
        damage: "€67.000 Gesamtschaden",
        description: "Ransomware verschlüsselte alle 3.200 Patientenakten einer Hausarztpraxis. Erpresser forderten 30.000€ Lösegeld.",
        consequences: [
          "12 Tage kompletter Praxisausfall - keine Patientenbehandlung möglich",
          "IT-Forensik und professionelle Datenrettung: 18.000€",
          "Betriebsunterbrechung und Umsatzverlust: 28.000€",
          "Neuinstallation aller Systeme und Sicherheitsupgrade: 12.000€",
          "DSGVO-Meldung und Patienteninformation: 9.000€"
        ]
      },
      {
        title: "Zahnarztpraxis Berlin - Röntgendaten verschlüsselt",
        location: "Berlin, 2024",
        damage: "€92.000 Gesamtschaden",
        description: "Ransomware über kompromittierte Praxissoftware. Alle digitalen Röntgenbilder und Behandlungspläne verschlüsselt.",
        consequences: [
          "47 laufende Implantologie-Behandlungen unterbrochen",
          "Lösegeldzahlung: 35.000€ (Versicherung übernahm)",
          "Datenwiederherstellung trotz Zahlung: 23.000€",
          "19 Tage Teilausfall der digitalen Systeme",
          "Patientenabwanderung und Reputationsschaden"
        ]
      }
    ],
    insuranceProtection: {
      title: "So schützt die Cyberversicherung bei Ransomware",
      coverages: [
        {
          name: "Cyber-Erpressungsschutz",
          description: "Übernahme von Lösegeldzahlungen nach Prüfung durch Experten. Professionelle Verhandlungsführung mit Erpressern.",
          example: "Lösegeld 30.000€ wird bezahlt, gleichzeitig Datenrettung eingeleitet"
        },
        {
          name: "IT-Forensik & Datenrettung",
          description: "Sofortige Beauftragung spezialisierter IT-Forensiker. Professionelle Entschlüsselungsversuche und Systemwiederherstellung.",
          example: "24/7-Notfall-Team innerhalb 2 Stunden vor Ort"
        },
        {
          name: "Betriebsunterbrechung",
          description: "Erstattung des Umsatzausfalls während der Ausfallzeit. Deckung laufender Kosten wie Miete und Gehälter.",
          example: "18 Tage Ausfall = 35.000€ Umsatzverlust ersetzt"
        },
        {
          name: "Krisenmanagement",
          description: "PR-Beratung und Patientenkommunikation. Unterstützung bei DSGVO-Meldungen und Behördenkontakt.",
          example: "Professionelle Pressemitteilung und Patientenanschreiben"
        },
        {
          name: "Neuinstallation & Upgrade",
          description: "Kosten für komplette Systemwiederherstellung und Sicherheitsverbesserungen.",
          example: "Neue Firewall, Backup-System, Mitarbeiterschulungen"
        },
        {
          name: "Rechtsbeistand",
          description: "Anwaltskosten bei strafrechtlichen Ermittlungen oder zivilrechtlichen Folgen.",
          example: "Verteidigung bei Schadensersatzforderungen von Patienten"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "Hiscox CyberClear Plus",
        provider: "Hiscox",
        coverage: "Bis 100.000€ Erpressungsschutz",
        highlight: "Beste Lösegeldeckung für kleine Praxen"
      },
      {
        tariff: "Markel Cyber",
        provider: "Markel",
        coverage: "Bis 500.000€ Erpressungsschutz",
        highlight: "Top für MVZ und große Praxen"
      },
      {
        tariff: "HDI Cyber Protect",
        provider: "HDI",
        coverage: "Bis 250.000€ Erpressungsschutz",
        highlight: "Inkl. professionelle Verhandlungsführung"
      }
    ],
    prevention: {
      title: "Ransomware-Prävention: 10 wichtige Maßnahmen",
      measures: [
        {
          category: "Backup-Strategie",
          actions: [
            "Tägliche automatische Backups aller Patientendaten",
            "3-2-1-Regel: 3 Kopien, 2 verschiedene Medien, 1 offline",
            "Offline-Backups die nicht im Netzwerk erreichbar sind",
            "Regelmäßige Wiederherstellungstests der Backups"
          ]
        },
        {
          category: "E-Mail-Sicherheit",
          actions: [
            "Spam-Filter mit Ransomware-Erkennung aktivieren",
            "Mitarbeiter schulen: Keine unbekannten Anhänge öffnen",
            "Verdächtige Absender blockieren und melden",
            "Zweifaktor-Authentifizierung für E-Mail-Zugang"
          ]
        },
        {
          category: "Software & Updates",
          actions: [
            "Automatische Updates für Betriebssystem aktivieren",
            "Praxissoftware immer auf neuester Version halten",
            "Veraltete Software ohne Support entfernen",
            "Sicherheitspatches innerhalb 48h installieren"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Sollte ich das Lösegeld zahlen, wenn meine Praxis angegriffen wird?",
        answer: "Niemals eigenständig zahlen! Kontaktieren Sie sofort Ihre Cyberversicherung. Diese beauftragt professionelle Verhandler und IT-Forensiker. In vielen Fällen können Daten auch ohne Zahlung wiederhergestellt werden. Zudem gibt es keine Garantie, dass Erpresser nach Zahlung wirklich den Schlüssel liefern."
      },
      {
        question: "Wie schnell greift die Versicherung bei einem Ransomware-Angriff?",
        answer: "Moderne Cyberversicherungen bieten 24/7-Notfall-Hotlines. Nach Schadensmeldung erhalten Sie innerhalb von 2-4 Stunden Rückruf von IT-Forensikern. Diese beginnen sofort mit der Schadensanalyse und Datenrettung, während parallel die Versicherungsabwicklung läuft."
      },
      {
        question: "Deckt die Versicherung auch Lösegeldzahlungen ab?",
        answer: "Ja, gute Cyberversicherungen haben spezielle Cyber-Erpressungsdeckungen. Diese übernehmen nach sorgfältiger Prüfung durch Experten auch Lösegeldzahlungen, wenn dies der einzige Weg zur Datenwiederherstellung ist. Wichtig: Nie selbst zahlen, immer Versicherung einschalten!"
      },
      {
        question: "Sind auch Backups vor Ransomware geschützt?",
        answer: "Moderne Ransomware verschlüsselt auch Backups im Netzwerk. Deshalb sind offline Backups essentiell. Die Versicherung unterstützt bei der Implementierung sicherer Backup-Strategien und übernimmt Kosten für professionelle Backup-Lösungen nach einem Angriff."
      },
      {
        question: "Was kostet eine Cyberversicherung mit Ransomware-Schutz?",
        answer: "Für kleine Praxen ab 29€ monatlich mit 50.000€ Erpressungsschutz. Größere Praxen mit höheren Deckungen (250.000€+) zahlen 80-200€ pro Monat. Die Kosten sind minimal im Vergleich zum Schaden eines Ransomware-Angriffs (Ø 85.000€)."
      },
      {
        question: "Kann ich meine Praxis nach einem Angriff überhaupt weiterführen?",
        answer: "Ja! Mit Cyberversicherung und professioneller Hilfe sind 95% der Praxen nach 2-3 Wochen wieder voll funktionsfähig. Die Versicherung übernimmt alle Wiederherstellungskosten, Umsatzausfälle und unterstützt bei der Patientenkommunikation. Ohne Versicherung enden 23% der Angriffe in Praxisschließung."
      }
    ]
  },

  datenverlust: {
    name: "Datenverlust-Versicherung",
    slug: "datenverlust",
    title: "Datenverlust-Versicherung für Arztpraxen - Schutz für Patientendaten",
    metaDescription: "Cyberversicherung bei Datenverlust in Arztpraxen. Schutz vor versehentlicher Löschung, Hardware-Ausfall und Datendiebstahl. Professionelle Datenrettung inkl.",
    heroTitle: "Versicherungsschutz bei Datenverlust",
    heroSubtitle: "Professionelle Datenrettung und Schadensabsicherung für Arztpraxen",
    description: "Datenverlust kann durch technische Defekte, menschliche Fehler oder Cyberangriffe entstehen. Für Arztpraxen sind verlorene Patientendaten katastrophal: Behandlungen können nicht fortgesetzt werden, DSGVO-Strafen drohen, das Vertrauen der Patienten schwindet.",
    statistics: [
      {
        label: "Hauptursache",
        value: "67%",
        description: "Durch Hardware-Ausfall verursacht"
      },
      {
        label: "Wiederherstellungskosten",
        value: "12.000€",
        description: "Ø Kosten für professionelle Datenrettung"
      },
      {
        label: "Erfolgsquote ohne Backup",
        value: "38%",
        description: "Nur 38% der Daten wiederherstellbar"
      },
      {
        label: "DSGVO-Meldepflicht",
        value: "72h",
        description: "Frist für Behördenmeldung"
      }
    ],
    howItWorks: {
      title: "Wie kommt es zu Datenverlust?",
      description: "Die häufigsten Ursachen für Datenverlust in Arztpraxen:",
      steps: [
        {
          title: "Hardware-Ausfall",
          description: "Server oder Festplatten defekt. Ohne funktionierendes Backup sind alle Daten verloren."
        },
        {
          title: "Versehentliches Löschen",
          description: "Mitarbeiter löscht versehentlich Patientenakten oder überschreibt wichtige Daten."
        },
        {
          title: "Ransomware-Angriff",
          description: "Verschlüsselung durch Erpressungstrojaner macht Daten unzugänglich."
        },
        {
          title: "Naturkatastrophen",
          description: "Feuer, Wasserschaden oder Überschwemmung zerstört Server-Hardware."
        },
        {
          title: "Softwarefehler",
          description: "Defekte Updates oder Programmierfehler führen zu Datenbankkorruption."
        }
      ]
    },
    realCases: [
      {
        title: "Server-Crash in Gemeinschaftspraxis",
        location: "Hamburg, 2023",
        damage: "€45.000 Gesamtschaden",
        description: "Hauptserver fiel aus, Backup-System war seit 3 Monaten defekt. 8.500 Patientenakten betroffen.",
        consequences: [
          "Professionelle Datenrettung vom defekten Server: 18.000€",
          "Nur 62% der Daten wiederherstellbar",
          "14 Tage Praxisbetrieb mit Notfallsystem",
          "DSGVO-Meldung und Patienteninformation: 7.000€",
          "Neuaufbau der verlorenen Daten: 20.000€"
        ]
      },
      {
        title: "Wasserschaden zerstört IT-Raum",
        location: "Köln, 2024",
        damage: "€38.000 Gesamtschaden",
        description: "Rohrbruch über IT-Raum. Server und Backup-Festplatten durch Wasser zerstört.",
        consequences: [
          "Spezialisten für Wasser-beschädigte Hardware: 22.000€",
          "Cloud-Backup noch intakt, aber 5 Tage alt",
          "Verlust aktueller Abrechnungsdaten und Terminkalender",
          "Neue Server-Hardware: 12.000€",
          "Umsatzverlust durch fehlende Abrechnungsdaten: 4.000€"
        ]
      }
    ],
    insuranceProtection: {
      title: "Versicherungsschutz bei Datenverlust",
      coverages: [
        {
          name: "Professionelle Datenrettung",
          description: "Spezialisierte Dienstleister für Hardware-Datenrettung. Auch bei physisch beschädigten Datenträgern.",
          example: "Datenrettung von defekter Festplatte in Reinraumlabor"
        },
        {
          name: "Datenrekonstruktion",
          description: "Wiederherstellung von Datenbanken und korrupten Dateien durch Forensik-Experten.",
          example: "Rekonstruktion beschädigter Patientenakten aus Fragmenten"
        },
        {
          name: "Neuerfassung von Daten",
          description: "Kosten für manuelle Neuerfassung unwiederbringlicher Daten aus Papierakten.",
          example: "Digitalisierung von 2.000 Papierakten durch Dienstleister"
        },
        {
          name: "Benachrichtigungskosten",
          description: "DSGVO-konforme Patienteninformation und Behördenmeldung. Anwaltskosten inklusive.",
          example: "Anwaltliche Unterstützung bei Datenschutzbehörden-Verfahren"
        },
        {
          name: "Ersatzhardware",
          description: "Kosten für Notfall-Hardware und beschleunigte Lieferung bei Totalausfall.",
          example: "Express-Lieferung neuer Server innerhalb 24h"
        },
        {
          name: "Betriebsunterbrechung",
          description: "Umsatzausfall während der Datenwiederherstellung.",
          example: "Entschädigung für 10 Tage eingeschränkten Praxisbetrieb"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "Allianz Cyber Protect",
        provider: "Allianz",
        coverage: "Bis 150.000€ Datenrettung",
        highlight: "Inkl. weltweite Datenrettungs-Partner"
      },
      {
        tariff: "VHV Cyber Safe",
        provider: "VHV",
        coverage: "Bis 100.000€ Datenrettung",
        highlight: "Spezialisiert auf Arztpraxen"
      },
      {
        tariff: "AXA Cyber Risk",
        provider: "AXA",
        coverage: "Bis 200.000€ Datenrettung",
        highlight: "24/7 Notfall-Hotline für Datenrettung"
      }
    ],
    prevention: {
      title: "Datenverlust vorbeugen",
      measures: [
        {
          category: "Backup-Strategie (wichtigste Maßnahme!)",
          actions: [
            "Tägliche automatische Backups einrichten",
            "3-2-1-Regel: 3 Kopien, 2 Medientypen, 1 extern",
            "Monatliche Wiederherstellungstests durchführen",
            "Cloud-Backup als zusätzliche Absicherung"
          ]
        },
        {
          category: "Hardware-Überwachung",
          actions: [
            "SMART-Überwachung für Festplatten aktivieren",
            "Server-Hardware alle 4-5 Jahre erneuern",
            "Redundante Systeme (RAID) einsetzen",
            "Unterbrechungsfreie Stromversorgung (USV) installieren"
          ]
        },
        {
          category: "Zugriffsrechte & Schulung",
          actions: [
            "Nur autorisierte Personen können Daten löschen",
            "Papierkorb-Funktion mit 30-Tage-Aufbewahrung",
            "Mitarbeiterschulungen zu Datensicherheit",
            "Versionierung aktivieren (gelöschte Daten wiederherstellbar)"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Wie lange dauert professionelle Datenrettung?",
        answer: "Bei logischen Schäden (versehentliches Löschen, Softwarefehler): 2-5 Tage. Bei physischen Schäden (defekte Festplatte): 1-3 Wochen im Reinraumlabor. Die Cyberversicherung organisiert Express-Services für kritische Praxisdaten."
      },
      {
        question: "Kann man Daten immer zu 100% wiederherstellen?",
        answer: "Nein. Die Erfolgsquote hängt vom Schadensausmaß ab. Logische Schäden: 80-95% Erfolg. Physische Schäden: 40-70% Erfolg. Überschriebene Daten: kaum wiederherstellbar. Daher sind regelmäßige Backups unverzichtbar!"
      },
      {
        question: "Was kostet professionelle Datenrettung ohne Versicherung?",
        answer: "Standard-Datenrettung: 1.500-5.000€. Komplexe Fälle (Reinraumlabor): 5.000-25.000€. Express-Service: +50-100% Aufpreis. Für Arztpraxen mit großen Datenbanken schnell 15.000-30.000€. Die Cyberversicherung übernimmt diese Kosten vollständig."
      },
      {
        question: "Muss ich Datenverlust den Behörden melden?",
        answer: "Ja, bei personenbezogenen Gesundheitsdaten besteht DSGVO-Meldepflicht innerhalb 72 Stunden, wenn ein Risiko für Patienten besteht. Die Cyberversicherung stellt Datenschutz-Anwälte, die bei der korrekten Meldung helfen und Strafen minimieren."
      },
      {
        question: "Sind Cloud-Daten sicherer als lokale Server?",
        answer: "Cloud-Anbieter haben meist bessere Backup-Strategien und Redundanzen. Aber: Auch Cloud-Daten können durch Ransomware verschlüsselt oder versehentlich gelöscht werden. Ideal ist eine Hybrid-Lösung: Lokaler Server + Cloud-Backup. Die Versicherung deckt beide Szenarien ab."
      },
      {
        question: "Wie oft sollte ich Backups testen?",
        answer: "Mindestens vierteljährlich! 30% aller Praxen stellen bei einem Schadensfall fest, dass ihre Backups defekt sind. Führen Sie regelmäßige Wiederherstellungstests durch. Viele Versicherer bieten kostenlose Backup-Audits im Rahmen der Cyberversicherung an."
      }
    ]
  },

  "dsgvo-bussgelder": {
    name: "DSGVO-Bußgelder absichern",
    slug: "dsgvo-bussgelder",
    title: "DSGVO-Bußgeld-Versicherung für Arztpraxen - Datenschutzverstöße absichern",
    metaDescription: "Cyberversicherung schützt vor DSGVO-Bußgeldern bei Datenpannen in Arztpraxen. Bis zu 20 Mio. € Strafen möglich. Jetzt absichern ab 35€/Monat.",
    heroTitle: "Schutz vor DSGVO-Bußgeldern",
    heroSubtitle: "Absicherung gegen Datenschutzstrafen bei Cyberangriffen auf Arztpraxen",
    description: "Die DSGVO sieht drastische Strafen bei Datenschutzverletzungen vor: Bis zu 20 Millionen Euro oder 4% des Jahresumsatzes. Für Arztpraxen mit sensiblen Gesundheitsdaten sind die Anforderungen besonders hoch. Ein Cyberangriff kann schnell zu existenzbedrohenden Bußgeldern führen.",
    statistics: [
      {
        label: "Maximales Bußgeld",
        value: "20 Mio. €",
        description: "Oder 4% des Jahresumsatzes"
      },
      {
        label: "Meldepflicht",
        value: "72 Stunden",
        description: "Frist für Behördenmeldung bei Datenpanne"
      },
      {
        label: "Gesundheitsdaten",
        value: "Besondere Kategorie",
        description: "Höchste DSGVO-Schutzstufe"
      },
      {
        label: "Ø Bußgeld Arztpraxen",
        value: "125.000€",
        description: "Bei schweren Datenschutzverstößen 2024"
      }
    ],
    howItWorks: {
      title: "Wann drohen DSGVO-Bußgelder?",
      description: "Diese Verstöße führen zu Strafen:",
      steps: [
        {
          title: "Datenpanne nicht gemeldet",
          description: "Cyberangriff verschwiegen oder zu spät (>72h) bei Datenschutzbehörde gemeldet."
        },
        {
          title: "Keine TOMs",
          description: "Technische und organisatorische Maßnahmen (Verschlüsselung, Zugriffskontrolle) fehlen oder unzureichend."
        },
        {
          title: "Fehlende Dokumentation",
          description: "Keine Verarbeitungsverzeichnisse, keine Datenschutz-Folgenabschätzung durchgeführt."
        },
        {
          title: "Patienten nicht informiert",
          description: "Betroffene Patienten nicht über Datenpanne informiert, obwohl hohes Risiko besteht."
        },
        {
          title: "Grobe Fahrlässigkeit",
          description: "Offensichtliche Sicherheitslücken nicht geschlossen (z.B. veraltete Software, schwache Passwörter)."
        }
      ]
    },
    realCases: [
      {
        title: "Hausarztpraxis - 85.000€ DSGVO-Strafe",
        location: "Nordrhein-Westfalen, 2023",
        damage: "€95.000 inkl. Verfahrenskosten",
        description: "Nach Ransomware-Angriff Datenpanne 8 Tage zu spät gemeldet. Zudem fehlten grundlegende Sicherheitsmaßnahmen.",
        consequences: [
          "DSGVO-Bußgeld: 85.000€ wegen verspäteter Meldung",
          "Anwaltskosten Behördenverfahren: 8.500€",
          "Datenschutzbeauftragter musste nachträglich bestellt werden",
          "Umfassendes IT-Sicherheitskonzept verlangt",
          "Nachweispflicht über 2 Jahre - Folgekosten 12.000€"
        ]
      },
      {
        title: "MVZ - 180.000€ Strafe für Datenleck",
        location: "Bayern, 2024",
        damage: "€210.000 inkl. Schadensersatz",
        description: "12.500 Patientendatensätze gestohlen durch Sicherheitslücke. Patienten nicht informiert, nur auf Nachfrage.",
        consequences: [
          "DSGVO-Bußgeld: 180.000€ (4% des Jahresumsatzes)",
          "Schadensersatzforderungen von 47 Patienten: 28.000€",
          "Anwaltskosten und Gutachter: 15.000€",
          "Reputationsschaden - Patientenabwanderung 22%",
          "Kosten für PR-Krisenmanagement: 8.000€"
        ]
      }
    ],
    insuranceProtection: {
      title: "Cyberversicherung bei DSGVO-Verstößen",
      coverages: [
        {
          name: "Bußgeld-Übernahme",
          description: "Versicherung übernimmt DSGVO-Bußgelder bis zur Deckungssumme (meist 1-5 Mio. €).",
          example: "Bußgeld 125.000€ komplett von Versicherung bezahlt"
        },
        {
          name: "Rechtsberatung Datenschutz",
          description: "Spezialisierte Datenschutz-Anwälte unterstützen bei Behördenverfahren und Verteidigung.",
          example: "Anwalt verhandelt Bußgeld von 200.000€ auf 75.000€ runter"
        },
        {
          name: "Schnelle Meldung",
          description: "Versicherung koordiniert fristgerechte Behördenmeldung innerhalb 72 Stunden.",
          example: "Rechtskonforme Meldung 48h nach Vorfall - Strafminderung"
        },
        {
          name: "Patientenbenachrichtigung",
          description: "Organisation und Kosten für DSGVO-konforme Information betroffener Patienten.",
          example: "Anschreiben an 3.500 Patienten inkl. Rechtsberatung"
        },
        {
          name: "Schadensersatz Dritter",
          description: "Deckung von Schadensersatzforderungen betroffener Patienten.",
          example: "Schmerzensgeldforderungen von 25 Patienten à 1.200€"
        },
        {
          name: "Gutachten & Nachweise",
          description: "Kosten für IT-Sicherheitsgutachten und Nachweisdokumentation gegenüber Behörden.",
          example: "TÜV-Gutachten über verbesserte Sicherheitsmaßnahmen"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "Hiscox CyberClear",
        provider: "Hiscox",
        coverage: "Bis 3 Mio. € Bußgelder",
        highlight: "Spezialisiert auf DSGVO-Compliance"
      },
      {
        tariff: "Markel Cyber Excellence",
        provider: "Markel",
        coverage: "Bis 5 Mio. € Bußgelder",
        highlight: "Inkl. Datenschutz-Hotline 24/7"
      },
      {
        tariff: "AXA Cyber Plus",
        provider: "AXA",
        coverage: "Bis 10 Mio. € Bußgelder",
        highlight: "Für große MVZ und Kliniken"
      }
    ],
    prevention: {
      title: "DSGVO-Bußgelder vermeiden",
      measures: [
        {
          category: "Meldepflicht einhalten",
          actions: [
            "Notfallplan für Datenpannen erstellen",
            "Verantwortlichkeiten festlegen (wer meldet?)",
            "Mustervorlagen für Behördenmeldung vorbereiten",
            "72-Stunden-Frist kennen und einhalten!"
          ]
        },
        {
          category: "Technische Maßnahmen",
          actions: [
            "Verschlüsselung sensibler Patientendaten",
            "Zugriffskontrollen und Berechtigungskonzept",
            "Aktuelle Firewall und Virenschutz",
            "Regelmäßige Sicherheitsupdates"
          ]
        },
        {
          category: "Organisatorische Maßnahmen",
          actions: [
            "Datenschutzbeauftragten bestellen (ab 20 Personen)",
            "Verarbeitungsverzeichnis führen und aktuell halten",
            "Mitarbeiterschulungen zu Datenschutz",
            "Auftragsverarbeitungsverträge mit Dienstleistern"
          ]
        },
        {
          category: "Dokumentation",
          actions: [
            "Datenschutz-Folgenabschätzung bei Risiken",
            "Löschkonzept für alte Patientendaten",
            "Dokumentation aller Sicherheitsmaßnahmen",
            "Incident-Response-Plan schriftlich festhalten"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Werden DSGVO-Bußgelder wirklich von der Versicherung übernommen?",
        answer: "Ja, moderne Cyberversicherungen decken DSGVO-Bußgelder explizit ab. ABER: Nicht bei vorsätzlichen Verstößen! Wenn Sie wissentlich gegen Datenschutz verstoßen, zahlt keine Versicherung. Bei Verstößen durch Cyberangriffe oder Fahrlässigkeit greift die Versicherung bis zur Deckungssumme."
      },
      {
        question: "Wie hoch sind DSGVO-Bußgelder wirklich?",
        answer: "Theoretisch bis 20 Mio. € oder 4% des Jahresumsatzes. Praktisch bei Arztpraxen: 5.000-200.000€ je nach Schwere. Faktoren: Anzahl betroffener Patienten, Art der Daten, Vorsatz/Fahrlässigkeit, Kooperation mit Behörden. Ohne Versicherung existenzbedrohend!"
      },
      {
        question: "Muss ich als kleine Praxis wirklich einen Datenschutzbeauftragten haben?",
        answer: "Ja, sobald mehr als 20 Personen ständig mit personenbezogenen Daten arbeiten. Bei Arztpraxen zählen alle Mitarbeiter (Ärzte, MFA, Verwaltung). Viele Praxen müssen einen externen DSB bestellen (Kosten: 150-400€/Monat). Die Versicherung berät bei der Auswahl."
      },
      {
        question: "Was passiert, wenn ich eine Datenpanne nicht melde?",
        answer: "Das ist ein schwerer DSGVO-Verstoß! Wenn Behörden später davon erfahren, drohen deutlich höhere Strafen als bei rechtzeitiger Meldung. Zudem verlieren Sie möglicherweise den Versicherungsschutz. Immer innerhalb 72h melden - die Versicherung hilft dabei!"
      },
      {
        question: "Können Patienten mich nach einem Cyberangriff verklagen?",
        answer: "Ja, Patienten haben Anspruch auf Schadensersatz bei DSGVO-Verstößen. Besonders bei sensiblen Gesundheitsdaten (HIV-Status, psychische Erkrankungen) drohen hohe Schmerzensgelder. Die Cyber-Haftpflicht der Versicherung deckt diese Forderungen ab."
      },
      {
        question: "Wie teuer ist eine Cyberversicherung mit DSGVO-Schutz?",
        answer: "Kleine Praxen: 35-75€/Monat mit 1-3 Mio. € Bußgeldschutz. Größere Praxen/MVZ: 120-300€/Monat mit 5-10 Mio. € Deckung. Die Kosten sind minimal im Vergleich zu einem möglichen Bußgeld von 100.000€+."
      }
    ]
  },

  betriebsunterbrechung: {
    name: "Betriebsunterbrechungs-Versicherung",
    slug: "betriebsunterbrechung",
    title: "Cyber-Betriebsunterbrechung für Arztpraxen - Umsatzausfall absichern",
    metaDescription: "Betriebsunterbrechungsversicherung bei IT-Ausfall in Arztpraxen. Absicherung von Umsatzausfall, laufenden Kosten und Mehraufwendungen. Jetzt schützen.",
    heroTitle: "Betriebsunterbrechung durch Cyberangriffe",
    heroSubtitle: "Absicherung von Umsatzausfall und laufenden Kosten bei IT-bedingtem Praxisstillstand",
    description: "Wenn Ihre Praxis-IT durch Cyberangriffe ausfällt, können Sie keine Patienten behandeln - aber Ihre Kosten laufen weiter. Miete, Gehälter, Leasingraten müssen trotzdem bezahlt werden, während kein Umsatz erwirtschaftet wird. Die Cyber-Betriebsunterbrechungsversicherung schützt vor diesem finanziellen Desaster.",
    statistics: [
      {
        label: "Ø Ausfallzeit",
        value: "12 Tage",
        description: "Bei Cyberangriff ohne Notfallplan"
      },
      {
        label: "Umsatzverlust/Tag",
        value: "2.400€",
        description: "Durchschnittliche Arztpraxis"
      },
      {
        label: "Fixkosten weiter",
        value: "100%",
        description: "Miete, Gehälter, Leasing laufen"
      },
      {
        label: "Gesamtschaden",
        value: "45.000€",
        description: "Ø bei 14 Tagen Komplettausfall"
      }
    ],
    howItWorks: {
      title: "So entsteht Betriebsunterbrechung",
      description: "Cyber-bedingte Szenarien die zum Praxisstillstand führen:",
      steps: [
        {
          title: "Ransomware-Angriff",
          description: "Praxissoftware verschlüsselt - keine Patientenakten, keine Terminverwaltung, keine Behandlung möglich."
        },
        {
          title: "Server-Ausfall",
          description: "Zentraler Server durch Cyberangriff zerstört. Wiederherstellung dauert Tage bis Wochen."
        },
        {
          title: "Netzwerk-Kompromittierung",
          description: "IT-Forensiker raten zur kompletten Netzwerk-Abschaltung bis zur Bereinigung."
        },
        {
          title: "Cloud-Ausfall",
          description: "Ihr Cloud-Praxissystem durch DDoS-Attacke oder Hackerangriff nicht erreichbar."
        },
        {
          title: "Datenpanne-Untersuchung",
          description: "Behörden ordnen IT-Stilllegung an bis zur Aufklärung des Sicherheitsvorfalls."
        }
      ]
    },
    realCases: [
      {
        title: "Zahnarztpraxis 18 Tage lahmgelegt",
        location: "Stuttgart, 2024",
        damage: "€52.000 Umsatzausfall",
        description: "Ransomware verschlüsselte Praxissoftware. Wiederherstellung dauerte 18 Tage - Praxis nur mit Notbetrieb.",
        consequences: [
          "Umsatzausfall 18 Tage Vollausfall: 43.000€",
          "Fixkosten weitergelaufen: 9.000€ (Miete, Gehälter, Leasing)",
          "Stornierte Behandlungen/OPs: 28.000€ Zusatzverlust",
          "Patienten zur Konkurrenz abgewandert: langfristig 15%",
          "Versicherung erstattete 52.000€ Betriebsunterbrechung"
        ]
      },
      {
        title: "MVZ - Server-Hack zwingt zur Schließung",
        location: "Düsseldorf, 2023",
        damage: "€178.000 für 4 Standorte",
        description: "Hacker verschafften sich Zugang zum zentralen Server. IT-Forensiker rieten zur Komplett-Abschaltung für 21 Tage.",
        consequences: [
          "4 Standorte gleichzeitig betroffen - kompletter Stillstand",
          "Umsatzausfall 21 Tage: 156.000€",
          "Fixkosten 4 Standorte: 22.000€",
          "Notfall-IT-Infrastruktur auf Mietbasis: 12.000€",
          "Patienteninformation und Terminverschiebung: 8.000€"
        ]
      }
    ],
    insuranceProtection: {
      title: "Was deckt die Betriebsunterbrechungs-Versicherung?",
      coverages: [
        {
          name: "Umsatzausfall",
          description: "Erstattung des entgangenen Umsatzes während der Ausfallzeit basierend auf Vorjahreswerten.",
          example: "14 Tage Ausfall × 2.400€ Tagesumsatz = 33.600€ erstattet"
        },
        {
          name: "Laufende Fixkosten",
          description: "Miete, Gehälter, Leasingraten, Versicherungen werden übernommen, obwohl kein Umsatz.",
          example: "Miete 4.500€ + Gehälter 12.000€ für Ausfallzeit bezahlt"
        },
        {
          name: "Mehraufwendungen",
          description: "Kosten für Notfall-Lösungen wie gemietete IT-Systeme oder Ausweichräume.",
          example: "Miet-Server und Notfall-Praxissoftware: 8.000€"
        },
        {
          name: "Ersatzpersonal",
          description: "Zusätzliche Mitarbeiter zur Aufarbeitung des Behandlungsrückstaus nach Wiederherstellung.",
          example: "2 MFA für 4 Wochen zur Terminkoordination: 6.000€"
        },
        {
          name: "Wertverlust Warenlager",
          description: "Verdorbene Medikamente oder Materialien während längerer Ausfallzeit.",
          example: "Impfstoffe und temperaturempfindliche Medikamente: 3.500€"
        },
        {
          name: "Eilfrachten & Express",
          description: "Beschleunigte Lieferung von Ersatz-Hardware oder Software.",
          example: "Express-Lieferung neuer Server innerhalb 24h: 2.800€ Aufpreis"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "HDI Cyber BU Plus",
        provider: "HDI",
        coverage: "Bis 250.000€ Betriebsunterbrechung",
        highlight: "Wartezeit nur 8 Stunden"
      },
      {
        tariff: "Markel Cyber Income",
        provider: "Markel",
        coverage: "Bis 500.000€ Betriebsunterbrechung",
        highlight: "Bis zu 12 Monate Deckungszeitraum"
      },
      {
        tariff: "VHV Cyber Ausfall",
        provider: "VHV",
        coverage: "Bis 150.000€ Betriebsunterbrechung",
        highlight: "Speziell für Arztpraxen kalkuliert"
      }
    ],
    prevention: {
      title: "Betriebsunterbrechung minimieren",
      measures: [
        {
          category: "Notfallplan erstellen",
          actions: [
            "Business Continuity Plan mit IT-Notfallszenarien",
            "Alternative Arbeitsmöglichkeiten definieren (Papierakten?)",
            "Notfall-Kontakte: IT-Dienstleister, Versicherung, Ersatzhardware",
            "Regelmäßige Notfall-Übungen mit Team durchführen"
          ]
        },
        {
          category: "Technische Vorkehrungen",
          actions: [
            "Redundante Systeme: Backup-Server sofort einsatzbereit",
            "Cloud-Lösung als Ausweichmöglichkeit",
            "Offline-Backups für schnelle Wiederherstellung",
            "Schneller Internet-Zugang zu Cloud-Backups"
          ]
        },
        {
          category: "Vertragliche Absicherung",
          actions: [
            "Service-Level-Agreements mit IT-Dienstleistern",
            "Garantierte Reaktionszeiten unter 4 Stunden",
            "Ersatzhardware-Verfügbarkeit vertraglich sichern",
            "Priorisierte Behandlung im Notfall vereinbaren"
          ]
        },
        {
          category: "Prozess-Optimierung",
          actions: [
            "Behandlungsabläufe auch ohne IT durchdenkbar machen",
            "Papier-Notfallformulare für wichtigste Prozesse",
            "Patientenkommunikation ohne Praxissoftware vorbereiten",
            "Mobile Backup-Lösung (Tablets mit Offline-Funktion)"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Ab wann zahlt die Betriebsunterbrechungs-Versicherung?",
        answer: "Das hängt von der vereinbarten Wartezeit ab. Standard sind 8-24 Stunden IT-Ausfall. Bei Ransomware-Angriffen oft sofort ab Schadensmeldung. Die Versicherung zahlt dann für die gesamte Ausfallzeit bis zur Wiederherstellung des Normalbetriebs."
      },
      {
        question: "Wie wird mein Umsatzausfall berechnet?",
        answer: "Basis sind Ihre Vorjahresumsätze im entsprechenden Zeitraum. Beispiel: Ausfall im Mai 2024 → Vergleich mit Mai 2023. Dabei werden saisonale Schwankungen und Wachstumstrends berücksichtigt. Wichtig: Genaue Buchhaltung erleichtert die Schadensabwicklung erheblich."
      },
      {
        question: "Was ist, wenn ich während des Ausfalls Notbetrieb mache?",
        answer: "Teilbetrieb wird berücksichtigt. Wenn Sie trotz IT-Ausfall 30% Ihrer normalen Leistungen erbringen können, erstattet die Versicherung 70% des Umsatzausfalls plus die Mehrkosten für den Notbetrieb. Vollständiger Ersatz minus tatsächlich erwirtschafteter Umsatz."
      },
      {
        question: "Wie lange ist die maximale Deckung bei Betriebsunterbrechung?",
        answer: "Standard sind 12 Monate ab Schadenseintritt. Das bedeutet: Selbst wenn die IT-Wiederherstellung 6 Monate dauert und dann noch 6 Monate bis zur Normalisierung der Patientenzahlen, sind Sie durchgehend abgesichert. Für Kliniken gibt es auch 24-Monats-Deckungen."
      },
      {
        question: "Werden auch Aufträge übernommen, die ich ablehnen musste?",
        answer: "Ja, entgangene Umsätze durch abgesagte Termine und Behandlungen sind versichert. Bei Wahlarzt-/Privat-Behandlungen auch Verdienstausfall durch verschobene OPs. Dokumentieren Sie alle Absagen und Verschiebungen für die Schadensabrechnung."
      },
      {
        question: "Was kostet eine Cyber-Betriebsunterbrechungs-Versicherung?",
        answer: "Die Kosten hängen von Ihrer Praxisgröße und dem Jahresumsatz ab. Kleine Praxen: 20-40€/Monat für 100.000€ Deckung. Größere Praxen: 80-200€/Monat für 500.000€ Deckung. Ein einziger 2-wöchiger Ausfall kostet ohne Versicherung oft mehr als 5 Jahre Beiträge!"
      }
    ]
  },

  "patientendaten-diebstahl": {
    name: "Patientendaten-Diebstahl",
    slug: "patientendaten-diebstahl",
    title: "Versicherung gegen Patientendaten-Diebstahl - Schutz bei Datenklau",
    metaDescription: "Cyberversicherung bei Diebstahl von Patientendaten in Arztpraxen. Schutz vor DSGVO-Strafen, Schadensersatz und Reputationsschäden. Jetzt absichern.",
    heroTitle: "Patientendaten-Diebstahl absichern",
    heroSubtitle: "Umfassender Schutz bei gestohlenen Gesundheitsdaten und Identitätsdiebstahl",
    description: "Patientendaten sind auf dem Schwarzmarkt hochbegehrt: Diagnosen, Behandlungshistorien, Abrechnungsdaten werden für Identitätsdiebstahl, Erpressung oder Betrug missbraucht. Für Arztpraxen drohen DSGVO-Strafen bis 20 Mio. Euro, Schadensersatzforderungen und massiver Vertrauensverlust.",
    statistics: [
      {
        label: "Schwarzmarkt-Wert",
        value: "250€",
        description: "Pro Gesundheitsdatensatz im Darknet"
      },
      {
        label: "Betroffene Patienten",
        value: "Ø 4.800",
        description: "Pro Datendiebstahl in Arztpraxen"
      },
      {
        label: "DSGVO-Strafe",
        value: "Bis 20 Mio. €",
        description: "Bei unzureichenden Schutzmaßnahmen"
      },
      {
        label: "Patientenabwanderung",
        value: "35%",
        description: "Nach bekannt gewordenen Datendiebstahl"
      }
    ],
    howItWorks: {
      title: "So werden Patientendaten gestohlen",
      description: "Die häufigsten Angriffsmethoden:",
      steps: [
        {
          title: "SQL-Injection",
          description: "Hacker nutzen Sicherheitslücken in Praxissoftware, um direkt auf Datenbanken zuzugreifen."
        },
        {
          title: "Phishing-Angriffe",
          description: "Mitarbeiter gibt versehentlich Zugangsdaten preis - Täter loggt sich ein und kopiert Patientendaten."
        },
        {
          title: "Insider-Bedrohung",
          description: "Unzufriedene Mitarbeiter oder externe Dienstleister stehlen gezielt Daten."
        },
        {
          title: "Ransomware mit Exfiltration",
          description: "Moderne Ransomware kopiert erst Daten, dann verschlüsselt sie - doppelte Erpressung."
        },
        {
          title: "Unsichere Schnittstellen",
          description: "Schwachstellen in Anbindungen zu Laboren, Krankenhäusern oder Abrechnungsstellen."
        }
      ]
    },
    realCases: [
      {
        title: "8.500 Patientendatensätze im Darknet",
        location: "Frankfurt, 2023",
        damage: "€215.000 Gesamtschaden",
        description: "Hacker stahlen über unsichere VPN-Verbindung 8.500 Patientenakten inkl. Diagnosen, HIV-Status, psych. Behandlungen.",
        consequences: [
          "DSGVO-Bußgeld: 125.000€ wegen unzureichender Verschlüsselung",
          "Schadensersatz an 73 besonders betroffene Patienten: 52.000€",
          "Anwaltskosten und Gutachter: 18.000€",
          "Krisenkommunikation und PR-Beratung: 12.000€",
          "35% Patientenabwanderung im Folgejahr - Umsatzeinbuße langfristig"
        ]
      },
      {
        title: "MVZ - Datendiebstahl durch Ex-Mitarbeiter",
        location: "Hamburg, 2024",
        damage: "€98.000 + Strafverfahren",
        description: "Ehemaliger IT-Administrator kopierte vor Kündigung 12.000 Patientendatensätze und verkaufte sie.",
        consequences: [
          "Alle 12.000 Patienten mussten informiert werden: 22.000€",
          "DSGVO-Meldung und Behördenverfahren: 8.000€",
          "Anwaltskosten Strafverfahren gegen Ex-Mitarbeiter: 15.000€",
          "Identity-Monitoring-Service für Patienten 2 Jahre: 48.000€",
          "Neue Zugriffskontroll-Systeme und Schulungen: 25.000€"
        ]
      }
    ],
    insuranceProtection: {
      title: "Versicherungsschutz bei Patientendaten-Diebstahl",
      coverages: [
        {
          name: "DSGVO-Bußgelder",
          description: "Übernahme von Datenschutz-Strafen bei Diebstahl trotz angemessener Sicherheitsmaßnahmen.",
          example: "Bußgeld 150.000€ komplett von Versicherung übernommen"
        },
        {
          name: "Schadensersatz an Patienten",
          description: "Deckung von Schadensersatz- und Schmerzensgeldansprüchen betroffener Patienten.",
          example: "85 Patienten fordern je 800€ Schmerzensgeld = 68.000€ gedeckt"
        },
        {
          name: "Patientenbenachrichtigung",
          description: "Kosten für rechtskonforme Information aller betroffenen Patienten per Post.",
          example: "Anschreiben an 5.000 Patienten inkl. Anwaltskosten: 18.000€"
        },
        {
          name: "Identity-Monitoring",
          description: "Finanzierung von Identitätsschutz-Services für betroffene Patienten (1-2 Jahre).",
          example: "Credit-Monitoring für 3.000 Patienten: 42.000€"
        },
        {
          name: "Krisenmanagement & PR",
          description: "Professionelle Kommunikationsberatung zur Schadensbegrenzung und Vertrauenswiederherstellung.",
          example: "PR-Agentur und Krisenkommunikation: 15.000€"
        },
        {
          name: "Forensische Untersuchung",
          description: "IT-Forensiker ermitteln wie, wann und welche Daten gestohlen wurden.",
          example: "Forensik-Analyse 2 Wochen: 22.000€"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "Hiscox CyberClear Pro",
        provider: "Hiscox",
        coverage: "Bis 5 Mio. € Haftpflicht",
        highlight: "Inkl. 24-Monate Identity-Monitoring"
      },
      {
        tariff: "Markel Cyber Breach",
        provider: "Markel",
        coverage: "Bis 10 Mio. € Haftpflicht",
        highlight: "Spezialisiert auf Datendiebstahl-Szenarien"
      },
      {
        tariff: "AXA Cyber DataProtect",
        provider: "AXA",
        coverage: "Bis 3 Mio. € Haftpflicht",
        highlight: "Schnelle Schadenregulierung bei Datenklau"
      }
    ],
    prevention: {
      title: "Patientendaten vor Diebstahl schützen",
      measures: [
        {
          category: "Verschlüsselung",
          actions: [
            "Datenbankverschlüsselung aktivieren (AES-256)",
            "Verschlüsselte Übertragung (SSL/TLS) bei allen Verbindungen",
            "Festplatten-Verschlüsselung auf allen Geräten",
            "Verschlüsselte E-Mail-Kommunikation mit Patienten"
          ]
        },
        {
          category: "Zugriffskontrolle",
          actions: [
            "Rollenbasierte Zugriffsrechte (nicht jeder sieht alles)",
            "Zwei-Faktor-Authentifizierung für alle Nutzer",
            "Automatische Abmeldung nach Inaktivität",
            "Protokollierung aller Datenzugriffe (Audit-Logs)"
          ]
        },
        {
          category: "Mitarbeiter-Management",
          actions: [
            "Hintergrund-Checks bei Neueinstellungen",
            "Sofortige Sperrung bei Kündigung",
            "Regelmäßige Schulungen zu Datenschutz",
            "Verpflichtungserklärungen auf Vertraulichkeit"
          ]
        },
        {
          category: "Technische Überwachung",
          actions: [
            "Intrusion-Detection-Systeme installieren",
            "Ungewöhnliche Datenzugriffe automatisch melden",
            "Datenverlust-Prävention (DLP-Software)",
            "Regelmäßige Sicherheitsaudits durch Externe"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Wie erkenne ich, dass Patientendaten gestohlen wurden?",
        answer: "Warnsignale: Ungewöhnliche Datenzugriffe in Logs, Darknet-Monitoring findet Ihre Daten, betroffene Patienten melden verdächtige Vorfälle, Behörden informieren Sie, Erpresser kontaktieren Sie. Moderne Monitoring-Tools erkennen Datendiebstahl oft in Echtzeit. Die Versicherung bietet oft Darknet-Monitoring als Service an."
      },
      {
        question: "Muss ich alle Patienten informieren, auch wenn nur wenige betroffen sind?",
        answer: "Das hängt vom Risiko ab. Bei hohem Risiko (sensible Diagnosen, Finanzdaten): JA, Information Pflicht. Bei niedrigem Risiko: Nur Behördenmeldung. Die Datenschutzbehörde entscheidet. Die Versicherung stellt Datenschutz-Anwälte, die Sie beraten und bei Behördenkontakt unterstützen."
      },
      {
        question: "Wie viel Schmerzensgeld können Patienten fordern?",
        answer: "Je nach Datensensibilität: 500-5.000€ pro Patient. HIV-Daten, psych. Diagnosen, Suchterkrankungen = höhere Summen. Bei 1.000 betroffenen Patienten schnell 500.000-2.000.000€. Die Cyber-Haftpflicht der Versicherung deckt diese Forderungen bis zur Deckungssumme ab."
      },
      {
        question: "Was ist Identity-Monitoring und wer zahlt das?",
        answer: "Service der Patientendaten überwacht (Darknet, Kreditanfragen, verdächtige Aktivitäten) und bei Missbrauch warnt. Kostet pro Patient 10-20€/Jahr. Bei 5.000 Patienten über 2 Jahre = 100.000-200.000€. Gute Cyberversicherungen übernehmen diese Kosten zur Schadensminimierung."
      },
      {
        question: "Können gestohlene Daten wieder zurückgeholt werden?",
        answer: "Nein, einmal gestohlen bleiben die Daten in Händen der Kriminellen. Man kann nur die Weiterverbreitung versuchen zu stoppen (Darknet-Takedowns) und Schaden bei Patienten minimieren (Monitoring, Sperrungen). Daher ist Prävention so wichtig! Verschlüsselung macht gestohlene Daten zumindest unlesbar."
      },
      {
        question: "Welche Deckungssumme brauche ich für Datendiebstahl-Risiken?",
        answer: "Mindestens 1 Mio. € Cyber-Haftpflicht für kleine Praxen. Bei mehr als 3.000 Patienten: 3-5 Mio. €. MVZ und Kliniken: 10-50 Mio. €. Die Deckung sollte: DSGVO-Strafen + Schadensersatz + Identity-Monitoring + Anwaltskosten abdecken können."
      }
    ]
  },

  "phishing-schutz": {
    name: "Phishing-Schutz",
    slug: "phishing-schutz",
    title: "Phishing-Schutz für Arztpraxen - Versicherung gegen E-Mail-Betrug",
    metaDescription: "Cyberversicherung gegen Phishing-Angriffe auf Arztpraxen. Schutz vor E-Mail-Betrug, Trojaner-Downloads und Zugangsdaten-Diebstahl. Jetzt absichern ab 29€.",
    heroTitle: "Phishing-Schutz für Arztpraxen",
    heroSubtitle: "Absicherung gegen E-Mail-Betrug und Social-Engineering-Angriffe",
    description: "92% aller Cyberangriffe beginnen mit einer Phishing-E-Mail. Kriminelle geben sich als Kassenärztliche Vereinigung, Labor oder Lieferant aus und täuschen Mitarbeiter. Ein Klick genügt - Trojaner verbreitet sich, Zugangsdaten gestohlen, Praxis lahmgelegt. Cyberversicherung schützt vor den Folgen.",
    statistics: [
      {
        label: "Angriffsmethode Nr. 1",
        value: "92%",
        description: "Aller Cyberangriffe via Phishing"
      },
      {
        label: "Erfolgsquote",
        value: "30%",
        description: "Der MFA klicken auf Phishing-Links"
      },
      {
        label: "Ø Schaden pro Vorfall",
        value: "38.000€",
        description: "Ohne Versicherungsschutz"
      },
      {
        label: "Reaktionszeit",
        value: "< 24h",
        description: "Bis Trojaner sich ausbreitet"
      }
    ],
    howItWorks: {
      title: "So funktionieren Phishing-Angriffe",
      description: "Die perfide Täuschung in 5 Schritten:",
      steps: [
        {
          title: "Tarnung",
          description: "E-Mail sieht aus wie von KV, Labor, Arznei-Lieferant. Logo, Absender, Signatur perfekt gefälscht."
        },
        {
          title: "Dringlichkeit",
          description: "'Wichtig: Neue Abrechnungsmodalitäten' oder 'Dringend: Patientendaten prüfen' - künstlicher Zeitdruck."
        },
        {
          title: "Köder",
          description: "Link 'Hier klicken' oder Anhang 'Rechnung.pdf.exe' - sieht harmlos aus, enthält Schadsoftware."
        },
        {
          title: "Infektion",
          description: "Mitarbeiter klickt - Trojaner installiert sich, Zugangsdaten werden abgegriffen, Backdoor öffnet sich."
        },
        {
          title: "Ausbreitung",
          description: "Schadsoftware verbreitet sich im Netzwerk, verschlüsselt Daten (Ransomware) oder spioniert aus."
        }
      ]
    },
    realCases: [
      {
        title: "Fake-KV-Mail führt zu Ransomware",
        location: "München, 2024",
        damage: "€54.000 Gesamtschaden",
        description: "MFA öffnet vermeintliche E-Mail der KV Bayern. Anhang enthält Ransomware - gesamte Praxis verschlüsselt.",
        consequences: [
          "Ransomware verschlüsselt 3.200 Patientenakten",
          "IT-Forensik und Datenrettung: 19.000€",
          "14 Tage Praxisausfall: 28.000€ Umsatzverlust",
          "Lösegeldforderung 25.000€ (nicht gezahlt)",
          "Mitarbeiter-Schulungen nachträglich: 7.000€"
        ]
      },
      {
        title: "CEO-Fraud: 45.000€ Überweisung",
        location: "Berlin, 2023",
        damage: "€45.000 + Folgekosten",
        description: "Gefälschte E-Mail angeblich vom Praxisinhaber: 'Bitte dringend 45.000€ an Lieferant überweisen.' Sekretärin überweist.",
        consequences: [
          "45.000€ auf Betrüger-Konto überwiesen - unwiederbringlich",
          "Polizeiliche Ermittlungen erfolglos",
          "Neue Prozesse für Zahlungsfreigaben implementiert",
          "Versicherung erstattete 45.000€ (Cyber-Betrugsdeckung)",
          "Reputationsschaden minimal (intern geblieben)"
        ]
      }
    ],
    insuranceProtection: {
      title: "Versicherungsschutz bei Phishing-Schäden",
      coverages: [
        {
          name: "Ransomware-Folgeschäden",
          description: "Wenn Phishing zu Ransomware führt: Datenrettung, Betriebsunterbrechung, Lösegeldzahlung.",
          example: "Komplette Ransomware-Abwicklung 65.000€ übernommen"
        },
        {
          name: "Cyber-Betrug (CEO-Fraud)",
          description: "Erstattung betrügerisch überwiesener Gelder nach Social-Engineering-Angriffen.",
          example: "35.000€ Fehlüberweisung durch Phishing zurückerstattet"
        },
        {
          name: "Zugangsdaten-Missbrauch",
          description: "Schäden durch gestohlene Login-Daten (Datenlöschung, unbefugte Zugriffe, Datendiebstahl).",
          example: "Wiederherstellung manipulierter Patientendaten: 12.000€"
        },
        {
          name: "Forensik & Schadenanalyse",
          description: "IT-Experten untersuchen Angriffsweg, Schadensumfang und schließen Sicherheitslücken.",
          example: "5 Tage forensische Untersuchung: 18.000€"
        },
        {
          name: "Präventionsmaßnahmen",
          description: "Nach Phishing-Vorfall: Schulungen, neue Sicherheitssysteme, Spam-Filter-Upgrade.",
          example: "Anti-Phishing-Training für Team + Technik-Upgrade: 8.000€"
        },
        {
          name: "Rechtsberatung",
          description: "Anwaltskosten bei rechtlichen Folgen (DSGVO-Verfahren, Schadensersatzforderungen).",
          example: "Vertretung bei Datenschutzbehörde: 6.500€"
        }
      ]
    },
    bestTariffs: [
      {
        tariff: "HDI Cyber Social Engineering",
        provider: "HDI",
        coverage: "Bis 100.000€ Betrugsschäden",
        highlight: "Speziell für CEO-Fraud ausgelegt"
      },
      {
        tariff: "Hiscox CyberClear",
        provider: "Hiscox",
        coverage: "Bis 250.000€ Phishing-Folgen",
        highlight: "Inkl. kostenlose Mitarbeiter-Trainings"
      },
      {
        tariff: "Markel Cyber Awareness",
        provider: "Markel",
        coverage: "Bis 500.000€ Social Engineering",
        highlight: "Präventions-Tools inklusive"
      }
    ],
    prevention: {
      title: "Phishing-Angriffe erkennen und verhindern",
      measures: [
        {
          category: "Mitarbeiter-Schulungen (WICHTIGSTE Maßnahme!)",
          actions: [
            "Vierteljährliche Phishing-Awareness-Trainings",
            "Simulierte Phishing-Tests zur Sensibilisierung",
            "Klare Verhaltensregeln bei verdächtigen E-Mails",
            "Melde-Kultur: Verdächtige Mails an IT weiterleiten"
          ]
        },
        {
          category: "Technische Schutzmaßnahmen",
          actions: [
            "Professioneller Spam-Filter mit KI-Erkennung",
            "Link-Scanner in E-Mails aktivieren",
            "Anhänge in Sandbox öffnen (automatisch)",
            "DMARC/SPF/DKIM E-Mail-Authentifizierung"
          ]
        },
        {
          category: "Prozess-Sicherheit",
          actions: [
            "4-Augen-Prinzip bei Überweisungen > 5.000€",
            "Telefonische Rückbestätigung bei ungewöhnlichen Anfragen",
            "Keine Passwörter per E-Mail versenden",
            "Zugangsdaten regelmäßig ändern (alle 90 Tage)"
          ]
        },
        {
          category: "Erkennungsmerkmale trainieren",
          actions: [
            "Absender-Adresse genau prüfen (kv-bayrn.de statt kv-bayern.de)",
            "Rechtschreibfehler und schlechtes Deutsch beachten",
            "Unerwartete Anhänge nie öffnen",
            "Bei Zeitdruck skeptisch sein - Betrüger erzeugen Hektik"
          ]
        }
      ]
    },
    faqs: [
      {
        question: "Wie erkenne ich eine Phishing-E-Mail?",
        answer: "Warnsignale: Unbekannter/falscher Absender, Rechtschreibfehler, allgemeine Anrede ('Sehr geehrte Damen und Herren'), Zeitdruck ('sofort', 'dringend'), unerwartete Anhänge, Links die woanders hinführen (Maus drüber halten ohne Klicken). Im Zweifel: Nicht klicken, Absender telefonisch kontaktieren!"
      },
      {
        question: "Was soll ich tun, wenn ich auf einen Phishing-Link geklickt habe?",
        answer: "SOFORT: 1) Computer vom Netzwerk trennen (WLAN aus, Kabel raus), 2) IT-Dienstleister informieren, 3) Passwörter von anderem Gerät ändern, 4) Cyberversicherung kontaktieren. NICHT: Weiterarbeiten, abwarten, Computer herunterfahren (Beweise gehen verloren). Schnelligkeit ist entscheidend!"
      },
      {
        question: "Zahlt die Versicherung auch bei Mitarbeiter-Fahrlässigkeit?",
        answer: "Ja! Gute Cyberversicherungen decken auch fahrlässiges Verhalten ab (z.B. Klick auf Phishing-Mail). NICHT gedeckt: Vorsätzliches Handeln oder grob fahrlässige Ignoranz (z.B. trotz mehrfacher Warnungen keine Schulungen). Einfache Fahrlässigkeit ist aber versichert."
      },
      {
        question: "Werden betrügerische Überweisungen wirklich erstattet?",
        answer: "Ja, wenn die Überweisung durch Social-Engineering-Phishing veranlasst wurde und Sie Opfer einer professionellen Täuschung sind. Voraussetzung: Angemessene Prozesse waren vorhanden (z.B. 4-Augen-Prinzip). Versicherung prüft jeden Fall, aber Erstattungsquote bei echten Phishing-Fällen liegt bei 80-90%."
      },
      {
        question: "Wie oft sollten Mitarbeiter geschult werden?",
        answer: "Mindestens vierteljährlich! Phishing-Methoden ändern sich ständig. Ideal: Vierteljährliche Präsenzschulungen + monatliche simulierte Phishing-Tests. Viele Versicherer bieten kostenlose Online-Trainings im Rahmen der Cyberversicherung an. Geschulte Teams reduzieren Phishing-Erfolg um 70%!"
      },
      {
        question: "Reicht ein guter Spam-Filter als Schutz?",
        answer: "Nein! Spam-Filter blockieren ca. 95% der Phishing-Mails, aber 5% kommen durch. Bei 100 E-Mails/Tag sind das 5 potenzielle Bedrohungen. Kombination essentiell: Technischer Schutz (Filter) + Menschliche Wachsamkeit (Schulung) + Versicherung (falls doch was durchkommt). Kein 100%-Schutz ohne alle drei Säulen!"
      }
    ]
  }
};

export const getDamageType = (slug: string): DamageTypeData | undefined => {
  return damageTypes[slug];
};

export const getAllDamageTypes = (): DamageTypeData[] => {
  return Object.values(damageTypes);
};
